import {useRouter} from 'next/router';
import {Flex, Text} from '@chakra-ui/layout';
import {keyframes} from '@chakra-ui/system';

import {useTranslate} from '@eksab/i18n';
import {getLanguage} from '@eksab/util/helpers';

import type {ChallengeConfig, TriviaGame} from '@eksab/features/play/types';

interface ChallengeListProps {
  game: ChallengeConfig;
}

export function ListingHeader({game}: ChallengeListProps) {
  const {locale} = useRouter();

  const t = useTranslate();

  const isWe = game.name === 'trivia-misc-we';

  return (
    <Flex
      as="header"
      flexDir="column"
      gap={{base: 1, lg: 2}}
      justify="center"
      h={{base: 48, md: 60, lg: 72}}
      ps={{base: 6, md: 12, lg: 16}}
      pe={{base: 28, lg: 40}}
      py="4"
      overflow="hidden"
      pos="relative"
      isolation="isolate"
      _before={{
        content: '""',
        pos: 'absolute',
        insetBlock: 0,
        insetStart: 'auto',
        insetEnd: [2, 6, 10],
        width: [56, 72, 96, '24rem'],
        zIndex: -1,
        bg: game.image ? `url(${game.image.src}) no-repeat` : undefined,
        backgroundPositionX: getLanguage(locale) === 'ar' ? 'left' : 'right',
        backgroundPositionY: bgPositions[game.name] ?? 'center',
        backgroundSize: {base: '40%', md: `auto ${bgSizes[game.name] ?? '90%'}`},
        transform: 'auto',
        translateY: '100%',
        animation:
          game.topic === 'sports'
            ? `${launchAnimation} 1.5s 0.5s forwards cubic-bezier(0.45, 0.88, 1, -0.68)`
            : `${bounceAnimation} 0.5s forwards ease-in-out`,
      }}
      bg={brandGradients[game.name]}
      rounded={{base: '3xl', lg: '2.125rem'}}
    >
      <Text as="h1" fontSize={{base: '2xl', md: '3xl', lg: '4xl'}} color="white" fontWeight={{base: 700, md: 800}}>
        {t(`play.game.${game.name}.title`)}
      </Text>

      {!isWe && (
        <Text
          color={{base: '#e9fff4', md: 'white'}}
          fontSize={{base: 'sm', md: 'md', lg: 'xl'}}
          fontWeight="600"
          w="xl"
          maxW={['full', '75%']}
        >
          {t(`play.game.${game.name}.description`)}
        </Text>
      )}
    </Flex>
  );
}

const launchAnimation = keyframes`
  100% { transform: translateY(0); }
`;

const bounceAnimation = keyframes`
  0% { transform: scale(0.5); }
  80% { transform: scale(1.3); }
  100% { transform: scale(1); }
`;

const brandGradients: Record<TriviaGame, string> = {
  'trivia-sports': 'linear-gradient(180deg, #5ac57b, #7a7a7a)',
  'trivia-art': 'linear-gradient(180deg, #3ea9dd 50%, #ffff46)',
  'trivia-sports-derby': 'linear-gradient(180deg, #242535 60%,  #f41e47)',
  'trivia-art-kataflam': 'linear-gradient(180deg, #3ea9dd 50%, #ffff46)',
  'trivia-sports-sabaho-kora': 'linear-gradient(180deg, #ff8c00, #ff8c0080)',
  'trivia-sports-jos-footy': 'linear-gradient(to bottom, #368eac, #e7916d)',
  'trivia-art-beta3-aflam': 'linear-gradient(to bottom, #910a09, #fede27)',
  'trivia-sports-ahly': 'linear-gradient(to bottom, #910a09, #000000)',
  'trivia-art-rsff': 'linear-gradient(to bottom, #ea4b5e, #ea4b5e)',
  'trivia-sports-spl': 'linear-gradient(to bottom, #f29829, #6e9ccf)',
  'trivia-misc-ayman-abbas': 'linear-gradient(to bottom, #627383, #000000)',
  'trivia-art-watch-it': 'linear-gradient(to bottom, #dca523, #000000)',
  'trivia-misc-we': 'linear-gradient(to bottom, #6b24a4, #000000)',
  'trivia-misc-shark-tank': 'linear-gradient(to bottom, #000000, #33affa)',
  'trivia-misc-amr-diab': 'linear-gradient(to bottom, #22180f, #b9aca2)',
  'trivia-misc-amr-mansi': 'linear-gradient(to bottom, #000000, #ffffff)',
  'trivia-misc-mpm': 'linear-gradient(to bottom, #000000, #000000)',
  'trivia-misc-peace-cake': '#40277b',
  'trivia-art-mohamed-ramadan': 'linear-gradient(to bottom, #a35950, #fccb65)',
  'trivia-sports-boxing': 'linear-gradient(180deg, #090f41, #7a7a7a)',
  'trivia-sports-mma': 'linear-gradient(180deg, #ea4b5e, #000000)',
  'trivia-misc-general': 'linear-gradient(180deg, #5ac57b, #7a7a7a)',
  'trivia-misc-alix-earle': 'linear-gradient(180deg, #22824a, #9d9eae)',
  'trivia-misc-chd': 'linear-gradient(180deg, #66656c, #5e253a)',
};

const bgPositions: Partial<Record<TriviaGame, string>> = {
  'trivia-sports': 'bottom',
  'trivia-sports-sabaho-kora': 'bottom',
  'trivia-sports-jos-footy': 'bottom',
  'trivia-art-beta3-aflam': 'bottom',
};

const bgSizes: Partial<Record<TriviaGame, string>> = {
  'trivia-art-watch-it': '50%',
  'trivia-misc-we': '50%',
  'trivia-misc-shark-tank': '70%',
  'trivia-misc-mpm': '70%',
  'trivia-misc-amr-diab': '70%',
  'trivia-misc-amr-mansi': '80%',
  'trivia-misc-peace-cake': '50%',
  'trivia-art-mohamed-ramadan': '70%',
  'trivia-sports-boxing': '60%',
};
